import { Suspense, lazy, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import GavelRounded from "@mui/icons-material/GavelRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { useDispatch, useSelector } from "react-redux";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { PanelIconButton } from "../sidebar/shared/panelChrome";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../redux/actions/sidebarActions";
import { FORM, formSx, px } from "../../theme/formStyles";
import { status } from "../../theme/colors";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import {
  BACKING_EXCEPTION_CATEGORY,
  calculatedLabel,
  findBackingException,
  classifiesProduct,
  getClassificationOverrideState,
} from "./classificationOverrideUtils";

const BorrowerClassificationOverrideModal = lazy(
  () => import("./BorrowerClassificationOverrideModal"),
);

/** Underwriting / admin on a product that classifies — the backend gate too. */
export const useCanOverrideClassification = (loan) => {
  const { isUnderwriter, isAdmin } = usePermissionsHook();
  return (isUnderwriter || isAdmin) && classifiesProduct(loan?.loanProductType);
};

/**
 * "Override classification" — opens the class + justification modal. Renders
 * nothing for users who can't override or products that don't classify, and
 * with `hideWhenSet` nothing once an override exists (its own row has the
 * button then). `icon` renders it as a pencil icon button; `iconSx` styles
 * that button (e.g. sized to sit inside a chip).
 */
export const ClassificationOverrideButton = ({
  size = "small",
  label,
  hideWhenSet = false,
  icon = false,
  iconSx,
}) => {
  const loan = useSelector((state) => state.loanDrilldown);
  const canOverride = useCanOverrideClassification(loan);
  const [show, setShow] = useState(false);
  const { override } = getClassificationOverrideState(loan);
  if (!canOverride || (hideWhenSet && override)) return null;

  const text =
    label || (override ? "Edit override" : "Override classification");
  return (
    <>
      {icon ? (
        <PanelIconButton
          icon={EditOutlined}
          title={text}
          onClick={() => setShow(true)}
          sx={iconSx}
        />
      ) : (
        <Button
          size={size}
          variant="outlined"
          onClick={() => setShow(true)}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {text}
        </Button>
      )}
      {show && (
        <Suspense fallback={null}>
          <BorrowerClassificationOverrideModal onClose={() => setShow(false)} />
        </Suspense>
      )}
    </>
  );
};

/**
 * The override's exception, beside it. When an exception backs the override
 * (same rule as the backend's backing_exception — no stored link exists) it
 * shows that exception's status and opens it. Otherwise an amber chip opens Add
 * Exception prefilled from the override: Borrower Classification category, an
 * "override B → A+" description and the justification note as compensating
 * factors. `compact` drops the "· Request" (the tooltip says it).
 */
export const OverrideExceptionChip = ({ compact = false }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const { override, calculated, note } = getClassificationOverrideState(loan);
  if (!override) return null;

  // The backing exception, by the backend's own rule (there is no stored link).
  const backing = findBackingException(loan);
  if (backing) {
    const st = backing.exceptionStatus || "Open";
    const description =
      backing.exceptionDescription || backing.description || null;
    return (
      <Tooltip
        arrow
        title={
          <Box sx={{ whiteSpace: "pre-wrap" }}>
            {`Backed by a ${backing.exceptionCategory || "loan"} exception (${st}).`}
            {description ? `\n${description}` : ""}
            {"\nClick to open it."}
          </Box>
        }
      >
        <Box
          component="button"
          type="button"
          onClick={() => {
            dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
            dispatch(sidebarSetStateValue(backing));
          }}
          sx={{
            ...formSx.pill(st === "Approved" ? "success" : "neutral"),
            gap: px(4),
            border: 0,
            cursor: "pointer",
            fontFamily: "inherit",
            "& .MuiSvgIcon-root": { fontSize: 12 },
          }}
        >
          <GavelRounded /> Exception · {st}
        </Box>
      </Tooltip>
    );
  }

  // No backing exception: request one, prefilled from the override so the
  // backend's text match finds it even if the category is later changed.
  const requestException = () => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
    dispatch(
      sidebarSetStateValue({
        prefill: {
          exceptionCategory: BACKING_EXCEPTION_CATEGORY,
          exceptionDescription: `Borrower classification override ${
            calculated || "not yet calculated"
          } → ${override}`,
          ...(note ? { compensatingFactors: note } : {}),
        },
      }),
    );
  };

  return (
    <Tooltip
      arrow
      title="The override needs a Borrower Classification exception on the loan. Click to request one."
    >
      <Box
        component="button"
        type="button"
        onClick={requestException}
        sx={{
          ...formSx.pill("warning"),
          gap: px(4),
          border: 0,
          cursor: "pointer",
          fontFamily: "inherit",
          "& .MuiSvgIcon-root": { fontSize: 12 },
        }}
      >
        <WarningAmberRounded /> {compact ? "No exception" : "No exception · Request"}
      </Box>
    </Tooltip>
  );
};

/** "by Jane Doe · 10/2/26 3:14 PM" — who / when, server-stamped. */
const useStampLine = (by, at) => {
  const users = useSelector((state) => state.users);
  if (!by && !at) return null;
  const who = by ? getUserFullNameFromEmail(users, by) || by : null;
  return [who && `by ${who}`, at && formatDateTime(at, "datetime")]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Compact control for a field row (Loan Info), on one line: the no-exception
 * flag, then the override chip — the class (note + who / when on hover) with
 * the edit pencil inside it. The row label already says "Override" and the
 * calculated class has its own row above, so neither is repeated here.
 */
export const ClassificationOverrideInline = () => {
  const loan = useSelector((state) => state.loanDrilldown);
  const canOverride = useCanOverrideClassification(loan);
  const { override, note, by, at } = getClassificationOverrideState(loan);
  const stamp = useStampLine(by, at);

  return (
    <Flx ac g={0.75} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
      <OverrideExceptionChip compact />
      {override ? (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: px(2),
            height: 26,
            pl: px(10),
            pr: canOverride ? px(2) : px(10),
            borderRadius: px(13),
            bgcolor: status.info.bg,
            color: status.info.fg,
          }}
        >
          <Tooltip
            arrow
            title={
              note || stamp ? (
                <Box sx={{ whiteSpace: "pre-wrap" }}>
                  {note || "No justification on file."}
                  {stamp ? `\n${stamp}` : ""}
                </Box>
              ) : (
                ""
              )
            }
          >
            <Box
              component="span"
              sx={{ fontSize: 13, fontWeight: 700, cursor: "help" }}
            >
              {override}
            </Box>
          </Tooltip>
          <ClassificationOverrideButton
            icon
            iconSx={{
              width: 22,
              height: 22,
              borderRadius: px(11),
              color: "inherit",
              "&:hover": { bgcolor: status.info.fg + "1f" },
            }}
          />
        </Box>
      ) : (
        <Txt sx={formSx.meta}>None</Txt>
      )}
    </Flx>
  );
};

/**
 * The classification card block: "Calculated A (0 credit + 5 experience) ·
 * Approved override A+", the justification note with by / at, the
 * exception-backing chip, and the Override button for UW / admin.
 */
export const ClassificationOverrideSummary = () => {
  const loan = useSelector((state) => state.loanDrilldown);
  const canOverride = useCanOverrideClassification(loan);
  const { details, calculated, override, note, by, at } =
    getClassificationOverrideState(loan);
  const stamp = useStampLine(by, at);

  if (!override && !canOverride) return null;

  return (
    <Flx column g={0.75}>
      <Txt sx={formSx.meta}>
        {calculatedLabel(calculated, details)}
        {override ? (
          <>
            {" · "}
            <Box component="span" sx={{ color: FORM.color.ink, fontWeight: 600 }}>
              Approved override {override}
            </Box>
          </>
        ) : null}
      </Txt>
      {override && (
        <Box sx={{ ...formSx.note, py: px(FORM.s.s2), px: px(FORM.s.s3) }}>
          <Txt
            component="div"
            sx={{
              ...formSx.helper,
              mt: 0,
              color: note ? FORM.color.ink2 : FORM.color.ink3,
              whiteSpace: "pre-wrap",
            }}
          >
            {note || "No justification on file — entered before notes were required."}
          </Txt>
          {stamp && (
            <Txt component="div" sx={{ ...formSx.meta, mt: px(FORM.s.glue) }}>
              {stamp}
            </Txt>
          )}
        </Box>
      )}
      <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
        <OverrideExceptionChip />
        <ClassificationOverrideButton />
      </Flx>
    </Flx>
  );
};
